import React from 'react';
import { Leaf, Info } from 'lucide-react';
import RecipeSuggestionsWidget from '../widgets/RecipeSuggestionsWidget';
import WhereToBuyWidget from '../widgets/WhereToBuyWidget';
import SeasonalCalendarWidget from '../widgets/SeasonalCalendarWidget';
import NutritionInfoWidget from '../widgets/NutritionInfoWidget';
import RelatedContentWidget from '../widgets/RelatedContentWidget';

interface Product {
  id: string;
  name: string;
  category?: string;
  type?: string;
  description?: string;
  seasonalMonths?: number[];
  nutritionInfo?: {
    calories?: number;
    protein?: number;
    carbs?: number;
    fat?: number;
    fiber?: number;
    vitaminC?: number;
    [key: string]: any;
  };
  benefits?: string[];
  storageInfo?: string;
  tags?: string[];
}

interface RelatedItem {
  id: string;
  title?: string;
  name?: string;
  description?: string;
  url: string;
  type: 'recipe' | 'blog' | 'produce' | 'farm' | 'seasonal';
  image?: string;
  meta?: string;
}

interface ProduceSidebarProps {
  produce: Product;
  relatedProduce?: RelatedItem[];
  showRecipes?: boolean;
  showWhereToBuy?: boolean;
  showSeasonalCalendar?: boolean;
  showNutrition?: boolean;
}

const ProduceSidebar: React.FC<ProduceSidebarProps> = ({
  produce,
  relatedProduce = [],
  showRecipes = true,
  showWhereToBuy = true,
  showSeasonalCalendar = true,
  showNutrition = true,
}) => {
  const produceName = produce.name || 'Produce';
  const category = produce.category || produce.type || '';

  const getCategoryColor = (cat: string): string => {
    const lowerCat = cat.toLowerCase();
    if (lowerCat.includes('fruit')) return '#f59e0b';
    if (lowerCat.includes('vegetable')) return '#10b981';
    if (lowerCat.includes('herb')) return '#8b5cf6';
    return '#2D5A27';
  };

  const getCategoryIcon = (cat: string): string => {
    const lowerCat = cat.toLowerCase();
    if (lowerCat.includes('fruit')) return '🍎';
    if (lowerCat.includes('vegetable')) return '🥕';
    if (lowerCat.includes('herb')) return '🌿';
    return '🌱';
  };

  const styles = {
    sidebar: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '24px',
    },
    quickInfoWidget: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    quickInfoTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1a1a1a',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
    },
    quickInfoIcon: {
      marginRight: '12px',
      color: '#2D5A27',
    },
    categoryBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '8px 16px',
      borderRadius: '20px',
      fontWeight: '600',
      fontSize: '14px',
      color: '#fff',
      marginBottom: '16px',
    },
    infoSection: {
      marginBottom: '16px',
    },
    infoLabel: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#666',
      textTransform: 'uppercase' as const,
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
    },
    infoText: {
      fontSize: '14px',
      color: '#333',
      lineHeight: '1.6',
    },
    benefitsList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
    },
    benefitItem: {
      fontSize: '14px',
      color: '#333',
      lineHeight: '1.6',
      paddingLeft: '20px',
      position: 'relative' as const,
      marginBottom: '8px',
    },
    benefitBullet: {
      position: 'absolute' as const,
      left: '0',
      top: '0',
      color: '#2D5A27',
      fontWeight: '600',
    },
    tagsGrid: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '8px',
    },
    tag: {
      padding: '6px 12px',
      backgroundColor: '#f0f7ef',
      color: '#2D5A27',
      borderRadius: '16px',
      fontSize: '12px',
      fontWeight: '600',
      border: '1px solid #d4e7d2',
    },
    storageBox: {
      padding: '12px',
      backgroundColor: '#f0f7ef',
      borderLeft: '3px solid #2D5A27',
      borderRadius: '4px',
      fontSize: '14px',
      color: '#333',
      lineHeight: '1.6',
    },
  };

  return (
    <div style={styles.sidebar}>
      {/* Quick Info Widget */}
      <div style={styles.quickInfoWidget}>
        <h3 style={styles.quickInfoTitle}>
          <Leaf style={styles.quickInfoIcon} size={24} />
          About {produceName}
        </h3>

        {category && (
          <div
            style={{
              ...styles.categoryBadge,
              backgroundColor: getCategoryColor(category),
            }}
          >
            <span style={{ marginRight: '8px', fontSize: '18px' }}>
              {getCategoryIcon(category)}
            </span>
            {category}
          </div>
        )}

        {produce.description && (
          <div style={styles.infoSection}>
            <div style={styles.infoLabel}>
              <Info size={14} style={{ marginRight: '6px' }} />
              Description
            </div>
            <div style={styles.infoText}>{produce.description}</div>
          </div>
        )}

        {produce.benefits && produce.benefits.length > 0 && (
          <div style={styles.infoSection}>
            <div style={styles.infoLabel}>Health Benefits</div>
            <ul style={styles.benefitsList}>
              {produce.benefits.map((benefit, index) => (
                <li key={index} style={styles.benefitItem}>
                  <span style={styles.benefitBullet}>✓</span>
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        )}

        {produce.storageInfo && (
          <div style={styles.infoSection}>
            <div style={styles.infoLabel}>Storage Tips</div>
            <div style={styles.storageBox}>{produce.storageInfo}</div>
          </div>
        )}

        {produce.tags && produce.tags.length > 0 && (
          <div style={styles.infoSection}>
            <div style={styles.infoLabel}>Tags</div>
            <div style={styles.tagsGrid}>
              {produce.tags.map((tag, index) => (
                <span key={index} style={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recipe Suggestions - KEY for "Basil Example" */}
      {showRecipes && (
        <RecipeSuggestionsWidget
          ingredients={[produceName]}
          maxRecipes={6}
        />
      )}

      {/* Where to Buy Widget */}
      {showWhereToBuy && (
        <WhereToBuyWidget
          productName={produceName}
          maxFarms={6}
          showRating={true}
          showDistance={true}
        />
      )}

      {/* Seasonal Calendar */}
      {showSeasonalCalendar && produce.seasonalMonths && produce.seasonalMonths.length > 0 && (
        <SeasonalCalendarWidget
          produceName={produceName}
          seasonalMonths={produce.seasonalMonths}
          showLegend={true}
        />
      )}

      {/* Nutrition Info */}
      {showNutrition && (
        <NutritionInfoWidget
          produceName={produceName}
          showHealthScore={true}
        />
      )}

      {/* Related Produce */}
      {relatedProduce && relatedProduce.length > 0 && (
        <RelatedContentWidget
          items={relatedProduce}
          title="Similar Produce"
          contentType="produce item"
          maxItems={4}
          layout="list"
        />
      )}
    </div>
  );
};

export default ProduceSidebar;
